import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Box,
    Drawer,
    AppBar,
    Toolbar,
    Typography,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    useMediaQuery,
    useTheme,
    BottomNavigation,
    BottomNavigationAction,
    Paper,
    Divider,
    Avatar
} from '@mui/material';
import {
    Menu as MenuIcon,
    Dashboard as DashboardIcon,
    Receipt as ReceiptIcon,
    People as PeopleIcon,
    LocalShipping as ShippingIcon,
    Assessment as ReportsIcon,
    Settings as SettingsIcon,
    Description as InvoiceIcon,
    AttachMoney as MoneyIcon,
    Groups as MembersIcon,
    Close as CloseIcon,
    AutoGraph as BrandIcon,
    Store as StoreIcon,
    ExitToApp as LogoutIcon,
    AdminPanelSettings as AdminIcon,
    History as HistoryIcon,
    ExpandLess,
    ExpandMore
} from '@mui/icons-material';
import { Collapse } from '@mui/material';
import { useSettings } from '../contexts/SettingsContext';
import { useAuth } from '../contexts/AuthContext';

const drawerWidth = 260;

const menuItems = [
    { text: 'Brand Identity', icon: <BrandIcon />, path: '/branding' },
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    { text: 'Transactions', icon: <ReceiptIcon />, path: '/transactions' },
    { text: 'Invoices', icon: <InvoiceIcon />, path: '/invoices' },
    { text: 'Purchase Orders', icon: <ShippingIcon />, path: '/purchase-orders' },
    { text: 'Customers', icon: <PeopleIcon />, path: '/customers' },
    { text: 'Vendors', icon: <StoreIcon />, path: '/vendors' },
    { text: 'Members', icon: <MembersIcon />, path: '/members' },
    { text: 'Reports', icon: <ReportsIcon />, path: '/reports' },
    { text: 'Settings', icon: <SettingsIcon />, path: '/settings' },
];

const mobileNavItems = [
    { text: 'Home', icon: <DashboardIcon />, path: '/dashboard' },
    { text: 'Transactions', icon: <ReceiptIcon />, path: '/transactions' },
    { text: 'Invoices', icon: <InvoiceIcon />, path: '/invoices' },
    { text: 'More', icon: <MenuIcon />, path: 'menu' },
];

const Layout = ({ children }) => {
    console.log('Layout component rendering...');
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [mobileOpen, setMobileOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const { companyName } = useSettings();
    const { user, logout } = useAuth();
    const [adminOpen, setAdminOpen] = useState(true);

    const handleAdminClick = () => {
        setAdminOpen(!adminOpen);
    };

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const handleNavigation = (path) => {
        if (path === 'menu') {
            setMobileOpen(true);
        } else {
            navigate(path);
            setMobileOpen(false);
        }
    };

    const getCurrentNavValue = () => {
        const item = mobileNavItems.findIndex(item => location.pathname.startsWith(item.path));
        return item >= 0 ? item : 0;
    };

    const drawer = (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Logo/Header */}
            <Box sx={{
                p: 2.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: '1px solid',
                borderColor: 'rgba(0,0,0,0.05)'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {/* Premium Animated Helix Logo */}
                    <Box sx={{
                        position: 'relative',
                        width: 42,
                        height: 42,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        {/* Animated Helix Rings */}
                        <Box sx={{
                            position: 'absolute',
                            width: '100%',
                            height: '100%',
                            border: '1.5px solid transparent',
                            borderTopColor: 'primary.main',
                            borderBottomColor: 'secondary.main',
                            borderRadius: '50%',
                            animation: 'spin 3s linear infinite',
                            opacity: 0.6
                        }} />
                        <Box
                            component="img"
                            src="/buds-logo.png"
                            alt="Buds Logo"
                            sx={{
                                height: 28,
                                width: 28,
                                objectFit: 'contain',
                                zIndex: 1
                            }}
                            onError={(e) => { e.target.src = 'https://via.placeholder.com/28?text=B'; }}
                        />
                    </Box>
                    <Box>
                        <Box
                            component="img"
                            src="/buds-full-logo.png"
                            alt="Budscube"
                            sx={{
                                opacity: 0.8,
                                height: 20,
                                width: 'auto',
                                display: 'block',
                                mb: 0.5
                            }}
                        />
                        <Box>
                            <Typography variant="subtitle2" fontWeight={800} sx={{ lineHeight: 1, color: 'text.primary' }}>
                                Spicy <span style={{ color: '#666', fontWeight: 600 }}>Aliyadans</span>
                            </Typography>
                            {user && (
                                <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 0.5, fontWeight: 600 }}>
                                    {user.role}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </Box>
                {isMobile && (
                    <IconButton onClick={handleDrawerToggle} size="small">
                        <CloseIcon />
                    </IconButton>
                )}
            </Box>

            {/* Navigation */}
            <List sx={{ flex: 1, py: 2 }}>
                {menuItems.filter(item => {
                    if (item.text === 'Settings' && user?.role === 'VIEWER') {
                        return false;
                    }
                    return true;
                }).map((item) => (
                    <ListItem key={item.text} disablePadding sx={{ px: 1, mb: 0.5 }}>
                        <ListItemButton
                            onClick={() => handleNavigation(item.path)}
                            selected={location.pathname.startsWith(item.path)}
                            sx={{
                                borderRadius: 2,
                                '&.Mui-selected': {
                                    bgcolor: 'primary.main',
                                    color: 'white',
                                    '&:hover': {
                                        bgcolor: 'primary.dark',
                                    },
                                    '& .MuiListItemIcon-root': {
                                        color: 'white',
                                    },
                                },
                            }}
                        >
                            <ListItemIcon sx={{ minWidth: 40 }}>
                                {item.icon}
                            </ListItemIcon>
                            <ListItemText primary={item.text} primaryTypographyProps={{ fontWeight: 500 }} />
                        </ListItemButton>
                    </ListItem>
                ))}

                {/* Admin Links Tree */}
                {user && user.role === 'ADMIN' && (
                    <>
                        <Divider sx={{ my: 1, mx: 2 }} />
                        <ListItem disablePadding sx={{ px: 1, mb: 0.5 }}>
                            <ListItemButton onClick={handleAdminClick} sx={{ borderRadius: 2 }}>
                                <ListItemIcon sx={{ minWidth: 40 }}>
                                    <AdminIcon />
                                </ListItemIcon>
                                <ListItemText
                                    primary="Administration"
                                    primaryTypographyProps={{ fontWeight: 600, color: 'text.secondary', variant: 'body2' }}
                                />
                                {adminOpen ? <ExpandLess color="action" /> : <ExpandMore color="action" />}
                            </ListItemButton>
                        </ListItem>
                        <Collapse in={adminOpen} timeout="auto" unmountOnExit>
                            <List component="div" disablePadding>
                                <ListItemButton
                                    sx={{ pl: 4, borderRadius: 2, mb: 0.5, mx: 1 }}
                                    onClick={() => handleNavigation('/users')}
                                    selected={location.pathname.startsWith('/users')}
                                >
                                    <ListItemIcon sx={{ minWidth: 35 }}>
                                        <MembersIcon fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText primary="Users" primaryTypographyProps={{ variant: 'body2' }} />
                                </ListItemButton>
                                <ListItemButton
                                    sx={{ pl: 4, borderRadius: 2, mb: 0.5, mx: 1 }}
                                    onClick={() => handleNavigation('/audit-logs')}
                                    selected={location.pathname.startsWith('/audit-logs')}
                                >
                                    <ListItemIcon sx={{ minWidth: 35 }}>
                                        <HistoryIcon fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText primary="Audit Logs" primaryTypographyProps={{ variant: 'body2' }} />
                                </ListItemButton>
                            </List>
                        </Collapse>
                    </>
                )}
            </List>

            <Box sx={{ px: 2, pb: 2 }}>
                <ListItemButton
                    onClick={logout}
                    sx={{
                        borderRadius: 2,
                        bgcolor: 'error.lighter',
                        color: 'error.main',
                        '&:hover': { bgcolor: 'error.light', color: 'error.contrastText' }
                    }}
                >
                    <ListItemIcon><LogoutIcon color="inherit" /></ListItemIcon>
                    <ListItemText primary="Logout" />
                </ListItemButton>
            </Box>

            <Divider sx={{ opacity: 0.5 }} />

            {/* Footer */}
            <Box component="footer" className="app-footer" sx={{ p: 2, mt: 'auto' }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                    &copy; {new Date().getFullYear()}. All rights reserved.
                </Typography>
                <span className="app-footer-sep">|</span>
                <a
                    className="app-footer-link"
                    href="https://budscube.com"
                    target="_blank"
                    rel="noreferrer"
                    title="Company website"
                >
                    <img className="app-footer-logo" src="/buds-full-logo.png" alt="Company logo" />
                </a>
            </Box>
        </Box>
    );

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            {/* App Bar - Mobile Only */}
            {isMobile && (
                <AppBar
                    position="fixed"
                    sx={{
                        bgcolor: 'background.paper',
                        color: 'text.primary',
                        boxShadow: 1,
                    }}
                >
                    <Toolbar>
                        <IconButton
                            color="inherit"
                            edge="start"
                            onClick={handleDrawerToggle}
                            sx={{ mr: 2 }}
                        >
                            <MenuIcon />
                        </IconButton>
                        <Box sx={{ position: 'relative', width: 28, height: 28, mr: 1 }}>
                            <Box
                                component="img"
                                src="/buds-logo.png"
                                alt="Buds"
                                sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                            />
                        </Box>
                        <Typography variant="subtitle1" noWrap component="div" fontWeight={800} sx={{ color: 'primary.main', letterSpacing: 0.5 }}>
                            {companyName}
                        </Typography>
                    </Toolbar>
                </AppBar>
            )}

            {/* Sidebar Drawer */}
            <Box
                component="nav"
                sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
            >
                {/* Mobile Drawer */}
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{ keepMounted: true }}
                    sx={{
                        display: { xs: 'block', md: 'none' },
                        '& .MuiDrawer-paper': {
                            boxSizing: 'border-box',
                            width: drawerWidth,
                        },
                    }}
                >
                    {drawer}
                </Drawer>

                {/* Desktop Drawer */}
                <Drawer
                    variant="permanent"
                    sx={{
                        display: { xs: 'none', md: 'block' },
                        '& .MuiDrawer-paper': {
                            boxSizing: 'border-box',
                            width: drawerWidth,
                        },
                    }}
                    open
                >
                    {drawer}
                </Drawer>
            </Box>

            {/* Main Content */}
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    p: 0,
                    width: { md: `calc(100% - ${drawerWidth}px)` },
                    minWidth: 0,
                    mt: { xs: '64px', md: 0 },
                    mb: { xs: '56px', md: 0 },
                    minHeight: '100vh',
                    bgcolor: 'background.default',
                    overflowX: 'hidden',
                }}
            >
                {/* Global Desktop Header */}
                {!isMobile && (
                    <Box sx={{
                        px: 4,
                        py: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        bgcolor: 'white',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        mb: 1
                    }}>
                        <Typography variant="body2" color="text.secondary" fontWeight={500}>
                            Home / <span style={{ color: theme.palette.primary.main }}>{location.pathname.split('/')[1].charAt(0).toUpperCase() + location.pathname.split('/')[1].slice(1)}</span>
                        </Typography>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box
                                component="img"
                                src="/logo-spicy.png"
                                alt="Spicy Aliyadans"
                                sx={{ height: 45, width: 'auto', objectFit: 'contain' }}
                            />
                            <Typography variant="h6" fontWeight={800} color="primary.main" sx={{ letterSpacing: 0.5 }}>
                                SPICY ALIYADANS
                            </Typography>

                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ textAlign: 'right', display: { xs: 'none', sm: 'block' } }}>
                                <Typography variant="subtitle2" fontWeight={600}>
                                    {user?.full_name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {user?.username}
                                </Typography>
                            </Box>
                            <Avatar sx={{ bgcolor: 'secondary.main' }}>
                                {user?.full_name?.charAt(0) || 'U'}
                            </Avatar>
                        </Box>
                    </Box>
                )}

                <Box sx={{ p: { xs: 1.5, md: 2.5 }, flexGrow: 1, minWidth: 0, maxWidth: '100%', overflowX: 'hidden' }}>
                    {children}
                </Box>
            </Box >

            {/* Bottom Navigation - Mobile Only */}
            {
                isMobile && (
                    <Paper
                        sx={{
                            position: 'fixed',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            zIndex: theme.zIndex.appBar,
                        }}
                        elevation={3}
                    >
                        <BottomNavigation
                            value={getCurrentNavValue()}
                            onChange={(event, newValue) => {
                                handleNavigation(mobileNavItems[newValue].path);
                            }}
                            showLabels
                        >
                            {mobileNavItems.map((item) => (
                                <BottomNavigationAction
                                    key={item.text}
                                    label={item.text}
                                    icon={item.icon}
                                />
                            ))}
                        </BottomNavigation>
                    </Paper>
                )
            }
        </Box >
    );
};

export default Layout;
